import { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Pencil, Trash2, Plus, Save, X, Star, Sparkles, Instagram, UtensilsCrossed } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MENU_CATEGORIES = ["beverages", "appetizers", "breakfast", "salads", "sandwiches", "pasta", "pizza", "rice_bowls", "mains", "casa_loco", "desserts", "viennoiserie", "cookies_bread"];
const SPECIAL_CATEGORIES = ["plates", "sips", "bakes"];

const TabButton = ({ active, onClick, children, icon: Icon }) => (
  <button onClick={onClick} className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold tracking-wider uppercase transition-colors ${active ? "bg-[#C9A85A] text-[#191646]" : "text-white/50 hover:text-white border border-white/10 hover:border-white/30"}`}>
    {Icon && <Icon size={16} />} {children}
  </button>
);

const InputField = ({ value, onChange, placeholder, type = "text", className = "" }) => (
  <input type={type} value={value} onChange={onChange} placeholder={placeholder} className={`bg-white/5 border border-white/20 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#C9A85A] outline-none ${className}`} />
);

// ============= MENU TAB =============
const MenuTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", description: "", price: "", category: "beverages", image_url: "", is_featured: false });

  const fetchItems = async () => {
    try { const res = await axios.get(`${API}/admin/menu`); setItems(res.data); } catch (e) { toast.error("Failed to load menu"); } finally { setLoading(false); }
  };
  useEffect(() => { fetchItems(); }, []);

  const filtered = filterCat === "all" ? items : items.filter(i => i.category === filterCat);

  const saveEdit = async () => {
    try { await axios.put(`${API}/admin/menu/${editingId}`, editForm); toast.success("Updated"); setEditingId(null); fetchItems(); } catch (e) { toast.error("Failed"); }
  };
  const deleteItem = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await axios.delete(`${API}/admin/menu/${id}`); toast.success("Deleted"); fetchItems(); } catch (e) { toast.error("Failed"); }
  };
  const addItem = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/admin/menu`, { ...addForm, price: parseFloat(addForm.price) || 0 }); toast.success("Added"); setShowAdd(false); setAddForm({ name: "", description: "", price: "", category: "beverages", image_url: "", is_featured: false }); fetchItems(); } catch (e) { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setFilterCat("all")} className={`px-3 py-1 text-xs uppercase tracking-wider border transition-colors ${filterCat === "all" ? "bg-[#C9A85A] text-[#191646] border-[#C9A85A]" : "border-white/20 text-white/50 hover:border-[#C9A85A]"}`}>All ({items.length})</button>
          {MENU_CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setFilterCat(cat)} className={`px-3 py-1 text-xs uppercase tracking-wider border transition-colors ${filterCat === cat ? "bg-[#C9A85A] text-[#191646] border-[#C9A85A]" : "border-white/20 text-white/50 hover:border-[#C9A85A]"}`}>{cat.replace("_", " ")} ({items.filter(i => i.category === cat).length})</button>
          ))}
        </div>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 bg-[#C9A85A] text-[#191646] px-4 py-2 text-sm font-semibold hover:bg-white transition-colors" data-testid="admin-add-item-btn"><Plus size={16} /> Add Item</button>
      </div>

      {showAdd && (
        <div className="bg-white/5 border border-white/10 p-6 mb-6">
          <h3 className="text-lg font-semibold text-[#C9A85A] mb-4">Add Menu Item</h3>
          <form onSubmit={addItem} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <InputField value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} placeholder="Name" />
            <InputField value={addForm.description} onChange={e => setAddForm({ ...addForm, description: e.target.value })} placeholder="Description" />
            <InputField type="number" value={addForm.price} onChange={e => setAddForm({ ...addForm, price: e.target.value })} placeholder="Price (0 to hide)" />
            <select value={addForm.category} onChange={e => setAddForm({ ...addForm, category: e.target.value })} className="bg-white/5 border border-white/20 px-3 py-2 text-sm text-white">
              {MENU_CATEGORIES.map(c => <option key={c} value={c} className="bg-[#191646]">{c.replace("_", " ")}</option>)}
            </select>
            <InputField value={addForm.image_url} onChange={e => setAddForm({ ...addForm, image_url: e.target.value })} placeholder="Image URL" />
            <label className="flex items-center gap-2 text-sm text-white/60"><input type="checkbox" checked={addForm.is_featured} onChange={e => setAddForm({ ...addForm, is_featured: e.target.checked })} className="accent-[#C9A85A]" /> Chef's Pick</label>
            <div className="flex gap-2 md:col-span-3">
              <button type="submit" className="flex items-center gap-2 bg-[#C9A85A] text-[#191646] px-4 py-2 text-sm font-semibold"><Save size={14} /> Save</button>
              <button type="button" onClick={() => setShowAdd(false)} className="flex items-center gap-2 border border-white/20 text-white/60 px-4 py-2 text-sm"><X size={14} /> Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="text-center py-20 text-white/40">Loading...</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10 text-left text-white/40 text-xs uppercase tracking-wider"><th className="pb-3 pr-3">Img</th><th className="pb-3 pr-3">Name</th><th className="pb-3 pr-3">Cat</th><th className="pb-3 pr-3">Price</th><th className="pb-3 pr-3">Description</th><th className="pb-3 pr-3">Pick</th><th className="pb-3">Act</th></tr></thead>
            <tbody>
              {filtered.map(item => (
                <tr key={item.id} className="border-b border-white/5 hover:bg-white/5">
                  {editingId === item.id ? (
                    <>
                      <td className="py-2 pr-3"><img loading="lazy" src={editForm.image_url} alt="" className="w-10 h-10 object-cover" /></td>
                      <td className="py-2 pr-3"><InputField value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="w-full" /></td>
                      <td className="py-2 pr-3"><select value={editForm.category} onChange={e => setEditForm({ ...editForm, category: e.target.value })} className="bg-white/5 border border-[#C9A85A]/50 px-2 py-1 text-sm text-white">{MENU_CATEGORIES.map(c => <option key={c} value={c} className="bg-[#191646]">{c.replace("_"," ")}</option>)}</select></td>
                      <td className="py-2 pr-3"><InputField type="number" value={editForm.price} onChange={e => setEditForm({ ...editForm, price: parseFloat(e.target.value) })} className="w-20" /></td>
                      <td className="py-2 pr-3"><InputField value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} className="w-full" /></td>
                      <td className="py-2 pr-3"><input type="checkbox" checked={editForm.is_featured} onChange={e => setEditForm({ ...editForm, is_featured: e.target.checked })} className="accent-[#C9A85A]" /></td>
                      <td className="py-2"><button onClick={saveEdit} className="text-[#C9A85A] hover:text-white mr-2"><Save size={14} /></button><button onClick={() => setEditingId(null)} className="text-white/40 hover:text-red-400"><X size={14} /></button></td>
                    </>
                  ) : (
                    <>
                      <td className="py-2 pr-3"><img loading="lazy" src={item.image_url} alt={item.name} className="w-10 h-10 object-cover" /></td>
                      <td className="py-2 pr-3 text-white font-medium">{item.name}</td>
                      <td className="py-2 pr-3"><span className="text-[#C9A85A] text-xs uppercase tracking-wider">{item.category?.replace("_"," ")}</span></td>
                      <td className="py-2 pr-3 text-white/70">{item.price > 0 ? `₹${item.price}` : <span className="text-white/30">--</span>}</td>
                      <td className="py-2 pr-3 text-white/50 max-w-xs truncate">{item.description}</td>
                      <td className="py-2 pr-3">{item.is_featured && <Star size={12} className="text-[#C9A85A] fill-[#C9A85A]" />}</td>
                      <td className="py-2"><button onClick={() => { setEditingId(item.id); setEditForm({ name: item.name, description: item.description, price: item.price, category: item.category, image_url: item.image_url, is_featured: item.is_featured }); }} className="text-white/40 hover:text-[#C9A85A] mr-2"><Pencil size={12} /></button><button onClick={() => deleteItem(item.id, item.name)} className="text-white/40 hover:text-red-400"><Trash2 size={12} /></button></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============= SPECIALS TAB =============
const SpecialsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", description: "", category: "plates", price: "", month: "September", is_active: true });

  const fetchItems = async () => {
    try { const res = await axios.get(`${API}/admin/specials`); setItems(res.data); } catch (e) { toast.error("Failed to load specials"); } finally { setLoading(false); }
  };
  useEffect(() => { fetchItems(); }, []);

  const saveEdit = async () => {
    try { await axios.put(`${API}/admin/specials/${editingId}`, { ...editForm, price: parseFloat(editForm.price) || 0 }); toast.success("Updated"); setEditingId(null); fetchItems(); } catch (e) { toast.error("Failed"); }
  };
  const deleteItem = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await axios.delete(`${API}/admin/specials/${id}`); toast.success("Deleted"); fetchItems(); } catch (e) { toast.error("Failed"); }
  };
  const addItem = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/admin/specials`, { ...addForm, price: parseFloat(addForm.price) || 0 }); toast.success("Added"); setShowAdd(false); setAddForm({ name: "", description: "", category: "plates", price: "", month: "September", is_active: true }); fetchItems(); } catch (e) { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-white/50 text-sm">{items.length} specials ({items.filter(i => i.is_active).length} active)</p>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 bg-[#C9A85A] text-[#191646] px-4 py-2 text-sm font-semibold hover:bg-white transition-colors"><Plus size={16} /> Add Special</button>
      </div>

      {showAdd && (
        <div className="bg-white/5 border border-white/10 p-6 mb-6">
          <form onSubmit={addItem} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <InputField value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} placeholder="Item name" />
            <InputField value={addForm.description} onChange={e => setAddForm({ ...addForm, description: e.target.value })} placeholder="Description" />
            <select value={addForm.category} onChange={e => setAddForm({ ...addForm, category: e.target.value })} className="bg-white/5 border border-white/20 px-3 py-2 text-sm text-white">{SPECIAL_CATEGORIES.map(c => <option key={c} value={c} className="bg-[#191646]">{c}</option>)}</select>
            <InputField type="number" value={addForm.price} onChange={e => setAddForm({ ...addForm, price: e.target.value })} placeholder="Price (0 to hide)" />
            <InputField value={addForm.month} onChange={e => setAddForm({ ...addForm, month: e.target.value })} placeholder="Month (e.g., September)" />
            <label className="flex items-center gap-2 text-sm text-white/60"><input type="checkbox" checked={addForm.is_active} onChange={e => setAddForm({ ...addForm, is_active: e.target.checked })} className="accent-[#C9A85A]" /> Active</label>
            <div className="flex gap-2">
              <button type="submit" className="flex items-center gap-2 bg-[#C9A85A] text-[#191646] px-4 py-2 text-sm font-semibold"><Save size={14} /> Save</button>
              <button type="button" onClick={() => setShowAdd(false)} className="border border-white/20 text-white/60 px-4 py-2 text-sm"><X size={14} /></button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="text-center py-20 text-white/40">Loading...</div> : (
        <div className="space-y-3">
          {items.map(item => (
            <div key={item.id} className={`border p-4 flex items-start gap-4 ${item.is_active ? "border-white/10 bg-white/5" : "border-white/5 bg-white/[0.02] opacity-60"}`}>
              {editingId === item.id ? (
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <InputField value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
                  <InputField value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} />
                  <select value={editForm.category} onChange={e => setEditForm({ ...editForm, category: e.target.value })} className="bg-white/5 border border-[#C9A85A]/50 px-2 py-1 text-sm text-white">{SPECIAL_CATEGORIES.map(c => <option key={c} value={c} className="bg-[#191646]">{c}</option>)}</select>
                  <InputField type="number" value={editForm.price} onChange={e => setEditForm({ ...editForm, price: e.target.value })} placeholder="Price" />
                  <InputField value={editForm.month} onChange={e => setEditForm({ ...editForm, month: e.target.value })} />
                  <label className="flex items-center gap-2 text-sm text-white/60"><input type="checkbox" checked={editForm.is_active} onChange={e => setEditForm({ ...editForm, is_active: e.target.checked })} className="accent-[#C9A85A]" /> Active</label>
                  <div className="flex gap-2"><button onClick={saveEdit} className="text-[#C9A85A] hover:text-white"><Save size={14} /></button><button onClick={() => setEditingId(null)} className="text-white/40 hover:text-red-400"><X size={14} /></button></div>
                </div>
              ) : (
                <>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-[#C9A85A] text-xs uppercase tracking-wider font-semibold">{item.category}</span>
                      <span className="text-white/30 text-xs">{item.month}</span>
                      {!item.is_active && <span className="text-red-400/60 text-xs">Inactive</span>}
                    </div>
                    <h4 className="text-white font-medium">{item.name}{item.price > 0 && <span className="text-[#C9A85A] ml-2 text-sm">&#8377;{item.price}</span>}</h4>
                    <p className="text-white/50 text-sm mt-0.5">{item.description}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => { setEditingId(item.id); setEditForm({ name: item.name, description: item.description, category: item.category, price: item.price, month: item.month, is_active: item.is_active }); }} className="text-white/40 hover:text-[#C9A85A]"><Pencil size={14} /></button>
                    <button onClick={() => deleteItem(item.id, item.name)} className="text-white/40 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============= INSTAGRAM TAB =============
const getEmbedUrl = (postUrl) => {
  if (!postUrl) return null;
  const clean = postUrl.split("?")[0].replace(/\/$/, "");
  return `${clean}/embed/`;
};

const InstagramTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const fetchItems = async () => {
    try { const res = await axios.get(`${API}/admin/instagram`); setItems(res.data); } catch (e) { toast.error("Failed to load posts"); } finally { setLoading(false); }
  };
  useEffect(() => { fetchItems(); }, []);

  const addPost = async (e) => {
    e.preventDefault();
    if (!newUrl.includes("instagram.com")) { toast.error("Please enter a valid Instagram URL"); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/instagram`, { post_url: newUrl, post_type: newUrl.includes("/reel/") ? "reel" : "post" });
      toast.success("Post added");
      setNewUrl("");
      fetchItems();
    } catch (e) { toast.error("Failed to add"); }
    finally { setAdding(false); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Remove this post from the website?")) return;
    try { await axios.delete(`${API}/admin/instagram/${id}`); toast.success("Removed"); fetchItems(); } catch (e) { toast.error("Failed"); }
  };

  return (
    <div>
      {/* Simple add form */}
      <div className="bg-white/5 border border-white/10 p-6 mb-8">
        <h3 className="text-lg font-semibold text-[#C9A85A] mb-1">Add Instagram Post</h3>
        <p className="text-white/40 text-xs mb-4">Paste any Instagram post or reel URL. It will be embedded on the homepage automatically.</p>
        <form onSubmit={addPost} className="flex gap-3">
          <div className="flex-1 flex items-center bg-white/5 border border-white/20 px-3">
            <Instagram size={16} className="text-white/30 mr-2 flex-shrink-0" />
            <input
              type="text"
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              placeholder="https://www.instagram.com/reel/... or /p/..."
              required
              className="w-full bg-transparent py-2.5 text-sm text-white placeholder:text-white/30 outline-none"
              data-testid="admin-insta-url-input"
            />
          </div>
          <button type="submit" disabled={adding} className="flex items-center gap-2 bg-[#C9A85A] text-[#191646] px-6 py-2.5 text-sm font-semibold hover:bg-white transition-colors disabled:opacity-50 whitespace-nowrap" data-testid="admin-insta-add-btn">
            <Plus size={16} /> {adding ? "Adding..." : "Add Post"}
          </button>
        </form>
      </div>

      {/* Posts list */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-medium">{items.length} posts on website</h3>
      </div>

      {loading ? <div className="text-center py-20 text-white/40">Loading...</div> : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/20">
          <Instagram className="text-white/20 mx-auto mb-3" size={32} />
          <p className="text-white/40 text-sm">No posts yet. Paste an Instagram URL above to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => (
            <div key={item.id} className="border border-white/10 bg-white/5 flex items-center gap-4 p-4" data-testid={`admin-insta-row-${index}`}>
              {/* Mini embed preview */}
              <div className="w-20 h-20 flex-shrink-0 bg-white overflow-hidden hidden md:block">
                <iframe
                  src={getEmbedUrl(item.post_url)}
                  className="border-0 pointer-events-none"
                  style={{ width: 200, height: 200, transform: "scale(0.4)", transformOrigin: "top left" }}
                  scrolling="no"
                  loading="lazy"
                  title="Preview"
                />
              </div>

              {/* URL and info */}
              <div className="flex-1 min-w-0">
                <a href={item.post_url} target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#C9A85A] text-sm font-medium truncate block transition-colors">
                  {item.post_url}
                </a>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-[#C9A85A] text-xs uppercase tracking-wider">{item.post_type || "post"}</span>
                  <span className="text-white/30 text-xs">{item.is_active ? "Active" : "Inactive"}</span>
                </div>
              </div>

              {/* Delete */}
              <button
                onClick={() => deleteItem(item.id)}
                data-testid={`admin-insta-delete-${index}`}
                className="flex items-center gap-1.5 text-white/40 hover:text-red-400 hover:bg-red-400/10 px-3 py-2 border border-transparent hover:border-red-400/30 transition-all text-xs"
              >
                <Trash2 size={14} /> Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============= MAIN ADMIN PAGE =============
export default function AdminPage() {
  const [activeTab, setActiveTab] = useState("menu");

  return (
    <div className="min-h-screen bg-[#0d0b2b] text-white">
      <Toaster position="top-right" richColors />
      <header className="border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#C9A85A] tracking-wider" style={{ fontFamily: "'Cinzel', serif" }}>KIPLINGS ADMIN</h1>
            <p className="text-white/40 text-xs">Dashboard</p>
          </div>
          <a href="/" className="text-white/50 hover:text-[#C9A85A] text-sm transition-colors" data-testid="admin-back-to-site">View Website</a>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-8">
          <TabButton active={activeTab === "menu"} onClick={() => setActiveTab("menu")} icon={UtensilsCrossed}>Menu ({activeTab === "menu" ? "active" : ""})</TabButton>
          <TabButton active={activeTab === "specials"} onClick={() => setActiveTab("specials")} icon={Sparkles}>Specials</TabButton>
          <TabButton active={activeTab === "instagram"} onClick={() => setActiveTab("instagram")} icon={Instagram}>Instagram</TabButton>
        </div>

        {activeTab === "menu" && <MenuTab />}
        {activeTab === "specials" && <SpecialsTab />}
        {activeTab === "instagram" && <InstagramTab />}
      </div>
    </div>
  );
}
